import { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  FormErrorMessage,
  VStack,
  Divider,
  Flex,
  Heading,
  Icon,
  useToast,
  ChakraProvider,
  theme,
  Box,
  Spinner,
  Text,
} from "@chakra-ui/react";
import {
  AccountTypeLabels,
  BankDetail,
  Proposal,
} from "@/models/proposal.model";
import { Field, FieldProps, Form, Formik, FormikHelpers } from "formik";
import { MdAccountBalance } from "react-icons/md";
import { PaymentMethodTypes } from "@/models/investor.model";
import {
  mapPaymentMethodToPixKeyType,
  mapPixKeyTypeToLabel,
} from "./payMethodMapper";
import { getBanks } from "@/services/bank.service";
import { bankDetailModalValidationSchema } from "./BankDetailModalValidation";
import { getProposalById, updateProposalBankDetail } from "@/services/proposal.service";
import InputMask from "react-input-mask";
import { formatPhone } from "@/utils/formatPhone";
import { emailChecker } from "@/utils/EmailChecker";
import { cpfFormatter } from "@/utils/cpfFormatter";
import { useParams } from "react-router-dom";

interface BankDetailUpdateFormProps {
  isOpen: boolean;
  onClose: () => void;
  proposalId?: string
}

export default function BankDetailModal({
  isOpen,
  onClose,
  proposalId,
}: BankDetailUpdateFormProps) {
  const { id:paramId } = useParams<{ id: string }>();
  const finalId = proposalId ?? paramId ?? null;
  const [banks, setBanks] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPixKeyValid, setIsPixKeyValid] = useState(true);
  const toast = useToast();
  const [ proposal, setProposal ] = useState<Proposal | null>();

  const paymentMethods = proposal?.chosenFinancialInfo?.Product?.processor
    ?.investors?.[0]?.paymentMethods || [
    PaymentMethodTypes.BANK_ACCOUNT,
    PaymentMethodTypes.DOCUMENT,
    PaymentMethodTypes.EMAIL,
    PaymentMethodTypes.PHONE,
  ];

  const isOnlyBankAccount =
    paymentMethods.length === 1 &&
    paymentMethods[0] === PaymentMethodTypes.BANK_ACCOUNT;

  const overriddenValues = isOnlyBankAccount
    ? { pixKeyType: "BANK_ACCOUNT", pixKey: "" }
    : {};

  useEffect(() => {
    if (!finalId) return;
    
    const fetchBanks = async () => {
      try {
        const response = await getBanks(1, 22);
        setBanks(
          response.data.map((bank: any) => ({ id: bank.id, name: bank.name }))
        );
      } finally {
        setIsLoading(false);
      }
    };
    const fetchProposal = async () => {
      try {
        setIsLoading(true);
        const data = await getProposalById(finalId!);
        setProposal(data);
      } catch (error) {
        console.error("Erro ao buscar proposta:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchProposal()
      fetchBanks();
    }
  }, [isOpen]);

  const initialValues: BankDetail = {
    ...(proposal ? proposal.BankDetail : {}),
    ...overriddenValues,
    
  };

  const handleSubmit = async (
    values: BankDetail,
    actions: FormikHelpers<BankDetail>
  ) => {
    try {
      if (
        values.pixKeyType === "EMAIL" &&
        values.pixKey &&
        !emailChecker(values.pixKey)
      ) {
        setIsPixKeyValid(false);
        actions.setSubmitting(false);
        return;
      }
      let payload = { ...values };
      if (payload.pixKey === "PHONE" || payload.pixKey === "DOCUMENT") {
        payload.pixKey = payload.pixKey?.replace(/\D/g, "");
      }
      if (payload.pixKey === "EMAIL" && payload.pixKey) {
        payload.pixKey = payload.pixKey.toLowerCase();
      }
      await updateProposalBankDetail({
        ...payload,
        id: finalId!,
      });
      toast({
        title: "Dados Bancários Corrigidos",
        description: "Os Dados Bancários foram corrigidos com sucesso",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      onClose();
    } catch {
      toast({
        title: "Erro",
        description:
          "Houve um erro ao corrigir dados bancários. Por favor, tente novamente em instantes.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      actions.setSubmitting(false);
    }
  };

  const normalizeInput = (input: string) =>
    input
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
          { isLoading || !proposal?   (
            <ChakraProvider theme={theme}>
              <Box
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                padding="3em"
              >
                <VStack spacing={4}>
                    <Spinner size="xl" color="primary" thickness="4px" speed="0.65s" />
                    <Text fontSize="xl" fontWeight="medium">
                      Carregando...
                    </Text>
                </VStack>
              </Box>
            </ChakraProvider>) : (
            <>
        <ModalHeader>
          <Flex align="center" justify="space-between" width="100%">
            <Flex align="center">
              <Icon as={MdAccountBalance} boxSize={8} color="gray.500" mr={2} />
              <Heading size="md" color="gray.700" fontFamily="Lexend Deca">
                Dados Bancários do seu cliente
              </Heading>
            </Flex>
            <ModalCloseButton position="static" />
          </Flex>
        </ModalHeader>
        <Divider mb={6} />
        <Formik
          initialValues={initialValues}
          validationSchema={bankDetailModalValidationSchema}
          onSubmit={handleSubmit}
        >
          {(props) => (
            <Form>
              <ModalBody>
                <VStack spacing={4}>
                  <Field name="pixKeyType">
                    {({ field, form }: FieldProps) => (
                      <FormControl
                        isInvalid={
                          !!(form.errors.pixKeyType && form.touched.pixKeyType)
                        }
                      >
                        <FormLabel htmlFor="pixKeyType">
                          Chave Pix através
                        </FormLabel>
                        <Select
                          {...field}
                          id="pixKeyType"
                          variant="flushed"
                          onChange={(e) => {
                            field.onChange(e);
                            form.setFieldValue("pixKeyType", e.target.value);
                            setIsPixKeyValid(true);
                            if (e.target.value === "DOCUMENT") {
                              form.setFieldValue(
                                "pixKey",
                                proposal?.NaturalPerson?.document
                                  ? cpfFormatter(proposal.NaturalPerson.document)
                                  : ""
                              );
                            }
                          }}
                        >
                          {paymentMethods.map((method) => {
                            const pixKeyType =
                              mapPaymentMethodToPixKeyType(method);
                            const label = mapPixKeyTypeToLabel(pixKeyType);
                            return (
                              <option key={pixKeyType} value={pixKeyType}>
                                {label}
                              </option>
                            );
                          })}
                        </Select>
                        <FormErrorMessage>
                          {form.errors.pixKeyType as string}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  {props.values.pixKeyType === "BANK_ACCOUNT" ? (
                    <>
                      <Field name="bankCode">
                        {({ field, form }: FieldProps) => (
                          <FormControl
                            isInvalid={
                              !!(form.errors.bankCode && form.touched.bankCode)
                            }
                          >
                            <FormLabel htmlFor="bankCode">Banco</FormLabel>
                            <Select
                              {...field}
                              id="bankCode"
                              placeholder="Selecione o banco"
                              variant="flushed"
                              isDisabled={isLoading}
                            >
                              {banks.map((bank) => (
                                <option key={bank.id} value={bank.id}>
                                  {bank.name}
                                </option>
                              ))}
                            </Select>
                            <FormErrorMessage>
                              {form.errors.bankCode as string}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                      <Field name="accountType">
                        {({ field, form }: FieldProps) => (
                          <FormControl
                            isInvalid={
                              !!(
                                form.errors.accountType &&
                                form.touched.accountType
                              )
                            }
                          >
                            <FormLabel htmlFor="accountType">
                              Tipo de Conta
                            </FormLabel>
                            <Select
                              {...field}
                              id="accountType"
                              variant="flushed"
                              placeholder="Selecione o tipo de conta"
                            >
                              {Object.entries(AccountTypeLabels).map(
                                ([key, label]) => (
                                  <option key={key} value={key}>
                                    {label}
                                  </option>
                                )
                              )}
                            </Select>
                            <FormErrorMessage>
                              {form.errors.accountType as string}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                      <Field name="agency">
                        {({ field, form }: FieldProps) => (
                          <FormControl
                            isInvalid={
                              !!(form.errors.agency && form.touched.agency)
                            }
                          >
                            <FormLabel htmlFor="agency">Agência</FormLabel>
                            <Input
                              as={InputMask}
                              mask="9999"
                              maskChar=""
                              {...field}
                              id="agency"
                              placeholder="Digite a agência"
                              variant="flushed"
                            />
                            <FormErrorMessage>
                              {form.errors.agency as string}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                      <Field name="account">
                        {({ field, form }: FieldProps) => (
                          <FormControl
                            isInvalid={
                              !!(form.errors.account && form.touched.account)
                            }
                          >
                            <FormLabel htmlFor="account">
                              Número da Conta
                            </FormLabel>
                            <Input
                              {...field}
                              as={InputMask}
                              mask="999999999"
                              maskChar=""
                              id="account"
                              placeholder="Digite o número da conta"
                              variant="flushed"
                            />
                            <FormErrorMessage>
                              {form.errors.account as string}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                      <Field name="accountDigit">
                        {({ field, form }: FieldProps) => (
                          <FormControl
                            isInvalid={
                              !!(
                                form.errors.accountDigit &&
                                form.touched.accountDigit
                              )
                            }
                          >
                            <FormLabel htmlFor="accountDigit">Dígito</FormLabel>
                            <Input
                              {...field}
                              as={InputMask}
                              mask="9"
                              maskChar=""
                              id="accountDigit"
                              placeholder="Digite o dígito da conta"
                              variant="flushed"
                            />
                            <FormErrorMessage>
                              {form.errors.accountDigit as string}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    </>
                  ) : props.values.pixKeyType ? (
                    <Field name="pixKey">
                      {({ field, form }: FieldProps) => {
                        let mask = "";
                        if (props.values.pixKeyType === "PHONE") {
                          mask = "(99) 99999-9999";
                        }
                        if (props.values.pixKeyType === "DOCUMENT") {
                          mask = "999.999.999-99";
                        }
                        return (
                          <FormControl
                            isInvalid={
                              (!!form.errors.pixKey && !!form.touched.pixKey) ||
                              !isPixKeyValid
                            }
                          >
                            <FormLabel htmlFor="pixKey">Chave Pix</FormLabel>
                            <Input
                              as={InputMask}
                              mask={mask}
                              maskChar=""
                              {...field}
                              id="pixKey"
                              placeholder="Digite a chave pix"
                              variant="flushed"
                              onChange={(e) => {
                                let value = e.target.value;
                                if (props.values.pixKeyType === "DOCUMENT") {
                                
                                  value = proposal?.NaturalPerson?.document ? cpfFormatter(proposal.NaturalPerson.document) : cpfFormatter(value);
                                } else if (props.values.pixKeyType === "PHONE") {
                                  value = formatPhone(value);
                                } else if (props.values.pixKeyType === "EMAIL") {
                                  value = normalizeInput(value);
                                  setIsPixKeyValid(!!emailChecker(value));
                                }
                                form.setFieldValue("pixKey", value);
                              }}
                            />
                            <FormErrorMessage>
                              {(!isPixKeyValid &&
                                props.values.pixKeyType === "EMAIL" &&
                                "Email inválido") ||
                                (form.errors.pixKey as string)}
                            </FormErrorMessage>
                          </FormControl>
                        );
                      }}
                    </Field>
                  ) : null}
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button
                  colorScheme="primary"
                  mr={3}
                  isDisabled={props.isSubmitting}
                  type="submit"
                >
                  Corrigir
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
        </>
          )}
      </ModalContent>
    </Modal>
  );
}
